import React from 'react';

import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { reverse } from 'named-urls';

import ActionCell, { ActionConfig } from 'components/Grid/components/ActionCell';
import { ColumnActions } from 'components/Grid/types';

import { useStore } from 'utils/hooks/store';
import { canArchiveLibrary } from 'utils/roles/permissions';

import routes from 'core/routes';

import { ARCHIVE_CONFIRMATION_MESSAGE, CONTROL_FIELD } from '../../constants';
import { ControlRow } from '../../types';

type ControlActionCellProps = {
  rowData: ControlRow;
  actions: ColumnActions;
};

const ControlActionCell: React.FunctionComponent<ControlActionCellProps> = ({ rowData, actions: { refreshData } }) => {
  const { uuid, archivedAt, inUse } = rowData;
  const recordName = rowData[CONTROL_FIELD.name];
  const navigate = useNavigate();
  const { notification, session, controlDetails } = useStore();

  const canArchive = canArchiveLibrary(session.user) && !session.user?.readOnly;
  const isArchived = Boolean(archivedAt);

  const setArchived = async (archived: boolean): Promise<void> => {
    if (await controlDetails.archiveControl(uuid, archived)) {
      await refreshData();
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (await controlDetails.delete(uuid)) {
      notification.enqueueSuccessSnackbar('Control deleted successfully');
      await refreshData();
    } else if (controlDetails.deleteBlockedReason) {
      notification.enqueueErrorSnackbar('This control is in use, so it cannot be deleted. Archive it instead.');
    } else {
      notification.enqueueErrorSnackbar('The control could not be deleted. Something went wrong.');
    }
  };

  const actions: ActionConfig[] = [
    {
      id: 'open-control',
      label: 'Open',
      onClick: () => navigate(reverse(routes.dashboard.risks.controlDetails, { entityId: uuid })),
    },
    {
      id: 'archive-control',
      label: 'Archive',
      onClick: () => setArchived(true),
      visible: canArchive && !isArchived,
      confirmation: {
        type: 'simple',
        title: 'Archive control',
        message: ARCHIVE_CONFIRMATION_MESSAGE,
        confirmButtonLabel: 'Archive',
        hideRecoveryWarning: true,
      },
    },
    {
      id: 'unarchive-control',
      label: 'Unarchive',
      onClick: () => setArchived(false),
      visible: canArchive && isArchived,
    },
    {
      id: 'delete-control',
      label: 'Delete',
      onClick: handleDelete,
      visible: canArchive && !inUse,
      confirmation: {
        type: 'simple',
        title: 'Delete control',
        message: 'Are you sure you want to delete this control?',
        confirmButtonLabel: 'Delete',
      },
    },
  ];

  return <ActionCell actions={actions} recordName={typeof recordName === 'string' ? recordName : undefined} />;
};

export default observer(ControlActionCell);
